import { useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';
import { isAppMode, isMobileDevice } from '@/utils/pwa-utils';

const InactivityLogout = () => {
  const { logout, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const warningToastRef = useRef(null); // Para guardar la referencia del toast de warning
  const timeoutMinutesRef = useRef(10); // Default

  // Detectar si está en PWA standalone
  const isPWAStandalone = () => {
    return isAppMode() || isMobileDevice();
  };

  // Fetch inactivity timeout setting
  useEffect(() => {
    const fetchTimeout = async () => {
      try {
        const response = await axios.get(`${API}/settings/inactivity-timeout`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        timeoutMinutesRef.current = response.data.timeout_minutes;
      } catch (error) {
        // Keep default of 10 minutes
      }
    };

    if (token) {
      fetchTimeout();
    }
  }, [token]);

  const resetTimer = () => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    
    // Dismiss warning toast if exists
    if (warningToastRef.current) {
      toast.dismiss(warningToastRef.current);
      warningToastRef.current = null;
    }

    // Get timeout in milliseconds
    const timeoutMs = timeoutMinutesRef.current * 60 * 1000;
    const warningMs = timeoutMs - 60 * 1000; // Show warning 1 minute before

    // Set warning timer (1 minute before logout)
    if (timeoutMinutesRef.current > 1) {
      warningRef.current = setTimeout(() => {
        warningToastRef.current = toast.warning('Tu sesión expirará en 1 minuto por inactividad', {
          duration: 60000,
          action: {
            label: 'Seguir activo',
            onClick: () => resetTimer()
          }
        });
      }, warningMs);
    }

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      // Dismiss warning toast before showing expiry message
      if (warningToastRef.current) {
        toast.dismiss(warningToastRef.current);
        warningToastRef.current = null;
      }
      
      logout();
      navigate('/');
      toast.info('Tu sesión ha expirado por inactividad');
    }, timeoutMs);
  };

  useEffect(() => {
    // NO ejecutar deslogeo automático si está en PWA standalone
    if (!token || isPWAStandalone()) return;

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (warningToastRef.current) {
        toast.dismiss(warningToastRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return null;
};

export default InactivityLogout;
